import {
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Button from 'components/Button/Button';
import Modal from 'components/Modal/Modal';
import { DATA_SETS_ENUM_TYPES } from 'constants/dataSets';
import {
  NACE_CODE,
  NACE_SECTION,
  PROFIT_CENTRE_NAME,
  VALUE_CHAIN_LIST,
  VALUE_CHAIN_SECTION,
} from 'constants/formFields';
import { BUTTON_VARIANT, VALUE_CHAIN_UNIT_TYPES } from 'constants/interfaces';
import { STEPS_PROFIT_CENTER_MODAL } from 'constants/valueChain';
import { useAiSubScreenContext } from 'contexts/AiScreenContext';
import useDataSetsDate from 'hooks/useDataSetsDate';
import useFieldsErrors from 'hooks/useFieldsErrors';
import useGeoLocations from 'hooks/useGeoLocations';
import useScrollToError from 'hooks/useScrollToError';
import useValueChainColumns from 'hooks/useValueChainColumns';
import useValueChainPosition from 'hooks/useValueChainPosition';
import {
  useEnumQuery,
  useGetNaceCodeTreeQuery,
} from 'store/slices/dataSets/apis/dataSetsApi';
import { selectEnums, selectNaceCodes } from 'store/slices/dataSets/selectors';
import {
  useCreateProfitCenterMutation,
  useGetBoardColumnNamesQuery,
  useGetOrgStructureValueChainMapQuery,
  useLazyGetValueChainMapBoardQuery,
  useLazyGetValueChainMapBusinessModelQuery,
  useUpdateProfitCenterMutation,
} from 'store/slices/valueChainMap/apis/valueChainMapApi';
import getRequestDataForProfitCentreUpdate from 'utils/formHelpers/getProfitCentreDefaultValuesUpdate';
import { extractApiErrorMessage } from 'utils/helpers';

import { useAppDispatch } from '../../../../../../store/hooks/useApp';
import { setDataSetsState } from '../../../../../../store/slices/dataSets/slice';
import useCreateProfitCentreForm from '../../hooks/useCreateProfitCentreForm';
import ProfitCenterForm from './ProfitCenterForm';

import type { TypesStepsProfitCenterModal } from 'constants/interfaces';
import type { Path, PathValue } from 'react-hook-form';
import type { INaceExtractedValue } from 'store/slices/dataSets/interfaces/INaceExtractedValue';
import type { IProfitCenter } from 'store/slices/valueChainMap/interfaces/IProfitCenter';
import type { TCreateOperationsSchema, TCreateProfitCentreSchema } from 'utils/validators';
import type IProfitCenterModal from '../../interfaces/IProfitCenterModal';

import styles from '../sass/ProfitCentreModal.module.scss';

export default function ProfitCenterModal({
  closeProfitCenterModal,
  valueChainMapId,
  selectedProfitCennter,
  setSelectedProfitCenter,
  currentTargetSwimlaneId,
  valueChainForm,
}: IProfitCenterModal) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { setFieldsErrors } = useFieldsErrors();
  const modalRef = useRef<HTMLDivElement>(null);
  const [
    currentType, setCurrentType,
  ] = useState<TypesStepsProfitCenterModal>(STEPS_PROFIT_CENTER_MODAL.CREATE_PROFIT_CENTER);

  const { data: naceCodesData, isLoading: isNaceCodesTreeLoading } = useGetNaceCodeTreeQuery(undefined, {
    selectFromResult: (result) => ({ ...result, data: selectNaceCodes(result.data) }),
  });

  const { data: enumData } = useEnumQuery({
    types: [DATA_SETS_ENUM_TYPES.currency, DATA_SETS_ENUM_TYPES.ownership, DATA_SETS_ENUM_TYPES.valueChainSection,
      DATA_SETS_ENUM_TYPES.businessUnitType, DATA_SETS_ENUM_TYPES.assetCategory, DATA_SETS_ENUM_TYPES.ownedLeased],
  }, {
    selectFromResult: (result) => ({ ...result, data: selectEnums(result.data) }),
  });

  const {
    data: orgStructureData,
    isLoading: isOrgStructureLoading,
  } = useGetOrgStructureValueChainMapQuery({ valueChainMapId });

  const {
    data: boardColumnNamesData,
    isLoading: isBoardColumnNamesLoading,
  } = useGetBoardColumnNamesQuery({ valueChainMapId });
  const [fetchProfitCenterRecord] = useLazyGetValueChainMapBusinessModelQuery();
  const [fetchProfitCenterBoard] = useLazyGetValueChainMapBoardQuery();

  const handleGetCurrentColumn = (id?: number) => {
    let currentColumn: { id: number, name: string } = { id: 0, name: '' };
    if (id) {
      currentColumn = {
        id,
        name: boardColumnNamesData?.data?.find(
          (column) => column?.columnId === id,
        )?.columnName.split('. ')[1]
          || '',
      };
    }
    return currentColumn;
  };

  const {
    control,
    handleSubmit,
    setError,
    watch: profitCenterWatch,
    setValue: setProfitCenterValue,
    fieldsName,
    watch,
    setValue,
    trigger,
    formState,
  } = useCreateProfitCentreForm({
    recordId: selectedProfitCennter?.id,
    valueChainSectionCurrent: handleGetCurrentColumn(currentTargetSwimlaneId)?.name,
    orgStructureData: orgStructureData?.data,
  });

  const {
    handleGetVcOptions,
    vcOptionsList,
    listPosition,
  } = useValueChainPosition(watch(VALUE_CHAIN_LIST)?.value, valueChainMapId);

  const [createProfitCenter, { isLoading: isCreateProfitCenterLoading }] = useCreateProfitCenterMutation();
  const [updateProfitCenter, { isLoading: isUpdateProfitCenterLoading }] = useUpdateProfitCenterMutation();

  const isPageLoading = isCreateProfitCenterLoading || isUpdateProfitCenterLoading
  || isOrgStructureLoading || isNaceCodesTreeLoading || isBoardColumnNamesLoading;

  const {
    dateOpened,
    onDateOpenedChange,
  } = useDataSetsDate<TCreateProfitCentreSchema>({ watch, setValue });

  const {
    startDate: startDateProfitCenter,
    onStartDateChange: onProfitCenterStartDateChange,
  } = useDataSetsDate<TCreateProfitCentreSchema>({ watch: profitCenterWatch, setValue: setProfitCenterValue });

  const {
    onFindCountries, onFindStates, onFindCities, isStatesDisabled, onChangeCountry, onChangeState,
    isCitiesDisabled, defaultCountries,
  } = useGeoLocations<TCreateProfitCentreSchema>({
    watch, setValue,
  });
  const naceDivisions = (watch(NACE_SECTION) as INaceExtractedValue)?.divisions || [];

  const stepTitles: Record<TypesStepsProfitCenterModal, string> = {
    [STEPS_PROFIT_CENTER_MODAL.SELECT_PROFIT_CENTER]: t('valueChainMap.addProfitCentreToTheStructure'),
    [STEPS_PROFIT_CENTER_MODAL.CREATE_PROFIT_CENTER]: t('profitCentres.createProfitCentre.title'),
    [STEPS_PROFIT_CENTER_MODAL.SELECT_OWN_OPERATION]: t('valueChainMap.selectOwnOperation'),
    [STEPS_PROFIT_CENTER_MODAL.CREATE_OWN_OPERATION]: t('valueChainMap.createOwnOperation'),
  };

  const generateTitle = (step: TypesStepsProfitCenterModal) => {
    if (step === STEPS_PROFIT_CENTER_MODAL.CREATE_PROFIT_CENTER) {
      return selectedProfitCennter?.id
        ? t('profitCentres.createProfitCentre.editTitle')
        : t('profitCentres.createProfitCentre.title');
    }
    return stepTitles[step] || '';
  };

  // Open over the map: the assistant is told which unit is being edited, or that a new one is.
  useAiSubScreenContext({
    name: generateTitle(currentType),
    customData: {
      unitType: VALUE_CHAIN_UNIT_TYPES.PROFIT_CENTER,
      id: selectedProfitCennter?.id,
      name: selectedProfitCennter?.name,
    },
  });

  const { getColumnId } = useValueChainColumns({
    enumData,
    boardColumnNamesData,
  });

  const { scrollToErrors } = useScrollToError({
    formState,
    scrollContainerRef: modalRef,
    scrollContainerSelector: '[class*="OwnOperationModalFormWrapper"]',
  });

  const onProfitCentreSubmit = handleSubmit((values) => {
    if (selectedProfitCennter?.id) {
      updateProfitCenter({
        data: getRequestDataForProfitCentreUpdate({ values, recordId: `${selectedProfitCennter.id}`, valueChainForm }),
        valueChainMapId,
        columnId: getColumnId(`${watch(VALUE_CHAIN_LIST)?.label}. ${watch(VALUE_CHAIN_SECTION)?.label}`)?.toString(),
      })
        .unwrap()
        .then(() => {
          setCurrentType(STEPS_PROFIT_CENTER_MODAL.SELECT_PROFIT_CENTER);
          if (valueChainMapId) {
            setTimeout(() => {
              fetchProfitCenterRecord({ valueChainMapId });
              fetchProfitCenterBoard({ valueChainMapId });
            }, 10000);
          }
          closeProfitCenterModal();
        })
        .catch((errors) => {
          const hasFieldErrors = setFieldsErrors({ errors, setError, fieldsName });
          if (hasFieldErrors) {
            scrollToErrors();
          } else {
            toast.error(extractApiErrorMessage(errors) || t('common.unknownError'));
          }
        });
    } else {
      createProfitCenter({
        data: getRequestDataForProfitCentreUpdate({ values, valueChainForm }),
        valueChainMapId,
        columnId: getColumnId(`${watch(VALUE_CHAIN_LIST)?.label}. ${watch(VALUE_CHAIN_SECTION)?.label}`)?.toString()
        || currentTargetSwimlaneId?.toString(),
      })
        .unwrap()
        .then(({ data }) => {
          setSelectedProfitCenter({ id: data.id, uniqueIdentifier: data.uniqueIdentifier } as IProfitCenter);
          dispatch(setDataSetsState({
            selectedProfitCenter: { label: data.uniqueIdentifier, value: data.uniqueIdentifier },
          }));
          if (valueChainMapId) {
            setTimeout(() => {
              fetchProfitCenterRecord({ valueChainMapId });
              fetchProfitCenterBoard({ valueChainMapId });
            }, 10000);
          }
          closeProfitCenterModal();
        })
        .catch((errors) => {
          const hasFieldErrors = setFieldsErrors({ errors, setError, fieldsName });
          if (hasFieldErrors) {
            scrollToErrors();
          } else {
            toast.error(extractApiErrorMessage(errors) || t('common.unknownError'));
          }
        });
    }
  });

  const onNextButtonClick = () => {
    if (currentType !== STEPS_PROFIT_CENTER_MODAL.CREATE_PROFIT_CENTER) {
      setCurrentType(STEPS_PROFIT_CENTER_MODAL.CREATE_PROFIT_CENTER);
    }
  };

  const onNaceSelectionChange = () => {
    const emptyOption = null as PathValue<TCreateOperationsSchema, Path<TCreateOperationsSchema>>;
    setValue(NACE_CODE, emptyOption);
  };

  return (
    <Modal
      open
      isButtonsVisible={false}
      className={styles.modal}
      title={generateTitle(currentType)}
    >
      <div ref={modalRef}>
        {
          currentType === STEPS_PROFIT_CENTER_MODAL.CREATE_PROFIT_CENTER && (
            <ProfitCenterForm
              control={control}
              enumData={enumData}
              recordId={selectedProfitCennter?.id}
              startDate={startDateProfitCenter}
              onStartDateChange={onProfitCenterStartDateChange}
              isLoading={isPageLoading}
              modalRef={modalRef}
              dateOpened={dateOpened}
              onDateOpenedChange={onDateOpenedChange}
              logicalName={watch(PROFIT_CENTRE_NAME)}
              setValue={setValue}
              orgStructureData={orgStructureData?.data}
              trigger={trigger}
              naceCodesData={naceCodesData}
              onNaceSelectionChange={onNaceSelectionChange}
              naceDivisions={naceDivisions}
              onFindCountries={onFindCountries}
              onFindStates={onFindStates}
              onFindCities={onFindCities}
              isStatesDisabled={isStatesDisabled}
              onChangeCountry={onChangeCountry}
              onChangeState={onChangeState}
              isCitiesDisabled={isCitiesDisabled}
              defaultCountries={defaultCountries}
              vcOptionsList={vcOptionsList}
              handleGetVcOptions={handleGetVcOptions}
              listPosition={listPosition}
            />
          )
        }
        <div className={styles.buttons}>
          <Button
            title={t('common.cancel')}
            labelKey="common.cancel"
            variant={BUTTON_VARIANT.cancel}
            onClick={closeProfitCenterModal}
          />
          <div className={styles.buttonsRight}>
            {selectedProfitCennter?.id && currentType === STEPS_PROFIT_CENTER_MODAL.CREATE_PROFIT_CENTER ? (
              <Button
                title={t('common.save')}
                labelKey="common.save"
                onClick={onProfitCentreSubmit}
                isLoading={isPageLoading}
              />
            ) : null}
            {!selectedProfitCennter?.id && currentType === STEPS_PROFIT_CENTER_MODAL.CREATE_PROFIT_CENTER ? (
              <Button
                title={t('common.create')}
                labelKey="common.create"
                onClick={onProfitCentreSubmit}
                isLoading={isPageLoading}
              />
            ) : null}

            {currentType !== STEPS_PROFIT_CENTER_MODAL.CREATE_PROFIT_CENTER ? (
              <Button
                title={t('common.next')}
                labelKey="common.next"
                onClick={onNextButtonClick}
                isLoading={isPageLoading}
              />
            ) : null}
          </div>
        </div>
      </div>
    </Modal>
  );
}
